import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchAdminAnalytics } from "@/lib/api";
import UserBadge from "@/components/UserBadge";
import AdminToggle from "@/components/AdminToggle";
import MobileBarStats from "@/components/MobileBarStats";
import VoiceUsageCard from "@/components/VoiceUsageCard";
import { ChevronLeft, BarChart3, Loader2, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const StatCard = ({ label, value, tone }) => (
  <div className="border border-obsidian-600 p-5">
    <p className="font-sans text-[0.65rem] uppercase tracking-[0.24em] text-bone-500 mb-2">{label}</p>
    <p className={`font-serif text-3xl ${tone === "warn" ? "text-terracotta" : "text-bone-100"}`}>{value}</p>
  </div>
);

const MiniBar = ({ items, keyFn, valueFn, testId }) => {
  const max = Math.max(1, ...items.map(valueFn));
  return (
    <div className="space-y-2" data-testid={testId}>
      {items.map((it, i) => (
        <div key={i} className="flex items-center gap-3">
          <span className="w-24 font-sans text-xs text-bone-400 truncate">{keyFn(it)}</span>
          <div className="flex-1 h-2 bg-obsidian-800">
            <div
              className="h-full bg-terracotta"
              style={{ width: `${(valueFn(it) / max) * 100}%` }}
            />
          </div>
          <span className="w-8 font-sans text-xs text-bone-300 text-right">{valueFn(it)}</span>
        </div>
      ))}
    </div>
  );
};

export default function AdminAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        setData(await fetchAdminAnalytics(days));
      } catch {
        toast.error("Analytics load failed.");
      } finally {
        setLoading(false);
      }
    })();
  }, [days]);

  return (
    <div className="min-h-screen">
      <header className="px-6 md:px-12 py-6 flex items-center justify-between border-b border-obsidian-600">
        <Link to="/admin" className="inline-flex items-center gap-2 font-sans text-xs uppercase tracking-[0.24em] text-bone-400 hover:text-bone-100">
          <ChevronLeft className="h-3 w-3" /> Admin
        </Link>
        <div className="flex items-center gap-4"><AdminToggle /><UserBadge /></div>
      </header>

      <div className="max-w-6xl mx-auto px-6 md:px-12 pt-12 pb-24">
        <MobileBarStats />
        <VoiceUsageCard />
        <div className="flex items-center gap-3 mb-4">
          <BarChart3 className="h-5 w-5 text-terracotta" />
          <p className="font-sans uppercase tracking-[0.28em] text-xs text-terracotta">Analytics</p>
        </div>
        <div className="flex items-end justify-between mb-10 gap-4 flex-wrap">
          <h1 className="font-serif text-4xl text-bone-100">Umbra at a glance</h1>
          <div className="flex gap-2">
            {[7, 30, 90].map((d) => (
              <button
                key={d}
                data-testid={`analytics-range-${d}`}
                onClick={() => setDays(d)}
                className={`font-sans text-xs uppercase tracking-[0.2em] px-3 py-1.5 border transition-colors ${
                  days === d
                    ? "border-terracotta text-terracotta"
                    : "border-obsidian-600 text-bone-400 hover:text-bone-100"
                }`}
              >
                {d}d
              </button>
            ))}
          </div>
        </div>

        {loading || !data ? (
          <div className="flex items-center gap-3 text-bone-400"><Loader2 className="h-4 w-4 animate-spin" /> loading</div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10" data-testid="analytics-stats">
              <StatCard label="Total users" value={data.total_users} />
              <StatCard label="Subscribers" value={data.subscribers} />
              <StatCard label={`Active in ${days}d`} value={data.active_in_window} />
              <StatCard label="Distress events" value={data.distress_events_in_window} tone="warn" />
              <StatCard label="Total entries" value={data.total_entries} />
              <StatCard label={`Whisper submits (${days}d)`} value={data.whisper_submits_in_window} />
              <StatCard label="Voices in use" value={data.voice_popularity.length} />
              <StatCard label="Chapters engaged" value={data.chapter_distribution.length} />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
              <div>
                <p className="font-sans uppercase tracking-[0.24em] text-[0.65rem] text-terracotta mb-4">Chapters engaged</p>
                <MiniBar
                  items={data.chapter_distribution}
                  keyFn={(i) => i.section_id}
                  valueFn={(i) => i.count}
                  testId="analytics-chapters"
                />
                {data.chapter_distribution.length === 0 && (
                  <p className="font-sans text-bone-500 text-xs">No entries in window.</p>
                )}
              </div>
              <div>
                <p className="font-sans uppercase tracking-[0.24em] text-[0.65rem] text-terracotta mb-4">Voice popularity</p>
                <MiniBar
                  items={data.voice_popularity}
                  keyFn={(i) => i.voice}
                  valueFn={(i) => i.count}
                  testId="analytics-voices"
                />
                {data.voice_popularity.length === 0 && (
                  <p className="font-sans text-bone-500 text-xs">No voice preferences saved.</p>
                )}
              </div>
            </div>

            <div>
              <p className="font-sans uppercase tracking-[0.24em] text-[0.65rem] text-terracotta mb-4">Entries per day</p>
              <div className="border border-obsidian-600 p-4">
                <MiniBar
                  items={data.entries_per_day}
                  keyFn={(i) => i.day.slice(5)}
                  valueFn={(i) => i.count}
                  testId="analytics-daily"
                />
                {data.entries_per_day.length === 0 && (
                  <p className="font-sans text-bone-500 text-xs">No activity in window.</p>
                )}
              </div>
            </div>

            <div className="mt-16 border-t border-obsidian-600 pt-8">
              <p className="font-sans uppercase tracking-[0.24em] text-[0.65rem] text-terracotta mb-2">Deep session data</p>
              <a
                href="https://app.posthog.com"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 font-serif italic text-bone-300 hover:text-terracotta"
              >
                Open PostHog dashboards <ExternalLink className="h-3 w-3" />
              </a>
              <p className="font-sans text-xs text-bone-500 mt-2">
                Full session recordings, funnels, retention cohorts, and page-level analytics.
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
